import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { number } from "../../config";
import CommonTabStrip from "../../shared/components/CommonTabStrip/CommonTabStrip";
import { addWorkflowForm, editWorkflowForm, getWorkflowDefaults, getWorkflowFormById } from "../../shared/services/workflow.services";
import { useWorkflowContext } from "../Workflow/WorkflowContextProvider";
import { VariableContextProvider } from "../Workflow/WorkflowVariables/context/variableContext";
import { usePayloadBuilder } from "../Workflow/WorkflowVariables/hooks/usePayloadBuilder";
import { useSelectedVariableValues } from "../Workflow/WorkflowVariables/hooks/workflowVariables.hooks";
import { useAdminVariableHelper } from "../Workflow/WorkflowVariables/hooks/workflowVariables.hooks";
import { normalizeMultiDimensionType } from "../Workflow/WorkflowVariables/workflowVariables.constants";
import FormBuilderHeader from "./FormBuilderHeader";
import FormBuilderSidebar from "./FormBuilderSidebar";
import FormCanvas from "./FormCanvas";
import { FormProvider, useFormContext } from "./FormContext";
import FormPropertiesWrapper from "./FormPropertiesWrapper";
import { FORM_ELEMENT_TYPES, FORM_TABS, FORM_TABS_KEYS } from "./FormsConstant";
import {
  FormCalculationProvider,
  FormChangeProvider,
  FormMetadataProvider,
  useFormChange,
  useFormInteraction,
  useFormMetadata,
} from "./contexts";
import "./styles/FormBuilder.scss";
import { mapSubsectionsToSchemaFormat } from "./utils/formSchemaMapping.utils";
import { createPayloadByType } from "./utils/payloadUtils";
import { transformFormDataWithSmartChips } from "./utils/smartChipsUtils";
import { collectUsedWorkflowVariableIds } from "./utils/usedWorkflowVariableIds";

/**
 * Form Builder Content Component
 * Contains the actual form builder logic and uses FormContext
 */
const FormBuilderContent = ({
  handleCloseModal,
  isNewForm,
  formId,
  workflowId,
  initialTitle,
  initialDescription,
  projectIdParam,
  onNavigateToWorkflowEditor,
  openFormSettingsOnRestore,
  onConsumeOpenFormSettingsRestore,
}) => {
  const { setOriginalState, checkForChanges, markAsSaved, hasChanges } = useFormChange();
  const { formTitle, setFormTitle, formDescription, setFormDescription, formPrivacy, setFormPrivacy, allowedProjectIds } = useFormMetadata();
  const { selectedElement, setSelectedElement, isSaving, setIsSaving, setSelectedSubsection } = useFormInteraction();
  const [formData, setFormData] = useState({
    title: formTitle,
    subsections: [],
  });
  const [currentFormData, setCurrentFormData] = useState(null); // Store the original form data for header switches
  const [isEditable, setIsEditable] = useState(true); // Lifted state for isEditable
  const { projectId } = useParams();
  const { companyId } = useSelector((state) => state.auth.user);
  const { id } = useSelector((state) => state.auth.user);
  const [formTabs, setFormTabs] = useState({
    ...FORM_TABS.SUBSECTIONS_OBJ,
    isHiddenSection: false,
    selected: number.ZERO,
  });
  const { setSelectedVariable, setIsNewWorkflowVariable, selectedVariable, customWorkflowVariables, workflow, setFormId } = useWorkflowContext();
  const dispatch = useDispatch();
  const { setSelectedVariableValues } = useSelectedVariableValues();

  /**
   * Set formId in WorkflowContext when formId is available
   * @author Muskan Thakur
   */
  useEffect(() => {
    if (formId) {
      setFormId(formId);
    }
  }, [formId]);

  useEffect(() => {
    if (initialTitle) {
      setFormTitle(initialTitle);
    }
    if (initialDescription !== undefined) {
      setFormDescription(initialDescription || "");
    }
  }, [initialTitle, initialDescription, setFormTitle, setFormDescription]);

  const { buildDropdownPayload, buildTextPayload, buildDatePayload, buildNumberPayload, buildPayload } = usePayloadBuilder();
  const { saveVariableForExistingWorkflow, saveVariableForNewWorkflow } = useAdminVariableHelper();
  // Helper function to create complete form state for comparison
  const createCompleteFormState = useCallback(
    () => ({
      title: formTitle,
      description: formDescription,
      content: formData,
      isEditable: isEditable,
    }),
    [formTitle, formDescription, formData, isEditable]
  );

  useEffect(() => {
    const fetchFormData = async () => {
      const response = await getWorkflowFormById({ id: formId, workflowId });

      // Service now handles smartChips merging automatically
      let formContent = response?.content;

      setFormData(formContent);
      setFormTitle(response?.title);
      setFormDescription(response?.description);
      setCurrentFormData(response);
      setIsEditable(response?.isEditable !== false); // Sync isEditable state
      setFormTabs((prev) => ({
        ...prev,
        isHiddenSection: response?.isHiddenSection ?? false,
      }));
      // Set original state for comparison after all state updates
      setTimeout(() => {
        setOriginalState({
          title: response?.title,
          description: response?.description,
          content: formContent,
          isEditable: response?.isEditable !== false,
        });
      }, 0);
    };

    if (formId && !isNewForm) {
      fetchFormData();
    } else {
      // Reset to fresh state for new forms
      const initialTitleLocal = initialTitle || "Untitled Form";
      const initialDescriptionLocal = initialDescription || "";
      const initialFormData = {
        title: initialTitleLocal,
        subsections: [],
        hiddenSubsections: [],
      };
      setFormData({
        ...initialFormData,
        subsections: [createNewSubsection()],
        hiddenSubsections: [createNewSubsection()],
      });
      setFormTitle(initialTitleLocal);
      setFormDescription(initialDescriptionLocal);
      setSelectedElement(null); // Clear any selected element
      setCurrentFormData(null); // Clear current form data for new forms
      setIsEditable(true); // Default to editable for new forms
      // Set original state for new forms
      setTimeout(() => {
        setOriginalState({
          title: initialTitleLocal,
          description: initialDescriptionLocal,
          content: initialFormData,
          isEditable: true,
        });
      }, 0);
    }
  }, [formId, isNewForm, setOriginalState]);

  // Check for changes whenever form state changes
  useEffect(() => {
    const currentState = createCompleteFormState();
    checkForChanges(currentState);
  }, [formTitle, formDescription, formData, isEditable, checkForChanges, createCompleteFormState]);

  // Add keyboard shortcut for Ctrl+S to save form
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Check if Ctrl+S (or Cmd+S on Mac)
      if ((event.ctrlKey || event.metaKey) && event.key === "s") {
        event.preventDefault(); // Prevent browser's default save behavior
        event.stopPropagation();
        // Only trigger save if there are changes and not currently saving
        if (hasChanges && !isSaving) {
          handleSave();
        }
      }
    };
    // Add event listener when component mounts
    document.addEventListener("keydown", handleKeyDown, true); // Use capture phase
    // Cleanup event listener when component unmounts
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [hasChanges, isSaving]);

  /**
   * Generate collision-free IDs (works even when multiple elements are created in the same tick)
   * @param {*} prefix
   * @returns
   */
  const generateId = (prefix) => {
    try {
      if (typeof window !== "undefined" && window.crypto?.randomUUID) {
        return `${prefix}_${window.crypto.randomUUID()}`;
      }
    } catch (_) {}
    return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  };

  const getAddWorkflowFormPayload = (formSchema) => {
    const payload = {
      workflowId: workflowId ?? null,
      content: formSchema,
      title: formTitle,
      description: formDescription,
      companyId: companyId,
      userId: id,
      projectId: projectIdParam || projectId,
      privacy: formPrivacy?.value,
    };

    if (formPrivacy?.value === 4 && allowedProjectIds && allowedProjectIds.length > 0) {
      payload.allowedProjectIds = allowedProjectIds;
    }

    return payload;
  };

  const getEditWorkflowFormPayload = (formSchema) => {
    return {
      id: formId,
      workflowId,
      content: formSchema,
      title: formTitle,
      description: formDescription,
      companyId: companyId,
      userId: id,
    };
  };

  // Handlers for different actions
  const handleSave = async () => {
    setIsSaving(true);

    // Transform form data to use smartChips structure
    const { transformedFormData, smartChips } = transformFormDataWithSmartChips(formData, customWorkflowVariables);

    // Generate the complete form schema
    const formSchema = {
      id: formId,
      title: formTitle,
      formBgColor: transformedFormData?.formBgColor,
      description: formDescription,
      created: new Date().toISOString(),
      smartChips: smartChips,
      subsections: mapSubsectionsToSchemaFormat(transformedFormData?.subsections),
      hiddenSubsections: mapSubsectionsToSchemaFormat(transformedFormData?.hiddenSubsections),
      settings: {
        submitButton: {
          text: "Submit",
          enabled: true,
        },
        confirmationMessage: "Thank you for your submission!",
        allowMultipleSubmissions: false,
      },
      metadata: {
        version: "1.0",
        lastModified: new Date().toISOString(),
        totalSubsections: transformedFormData?.subsections?.length ?? 0,
      },
    };

    try {
      let response;
      if (isNewForm && !formId) {
        response = await dispatch(addWorkflowForm(getAddWorkflowFormPayload(formSchema)));
        // Close modal after successful creation
      } else {
        response = await dispatch(editWorkflowForm(getEditWorkflowFormPayload(formSchema)));
        // Close modal after successful update
      }
      // Mark as saved with the new form state after successful save
      const newFormState = {
        title: formTitle,
        description: formDescription,
        content: formData,
        isEditable: isEditable,
      };
      markAsSaved(newFormState);
      if (isNewForm) {
        handleCloseModal();
      }
    } catch (error) {
      console.error("Error saving form:", error);
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Creates payload based on selectedVariable type using utility function
   * @returns {Object|null} The payload object or null if no selectedVariable
   * @author Abhishek Dhillon
   */
  const createVariablePayload = () => {
    const payloadBuilders = {
      buildDropdownPayload,
      buildTextPayload,
      buildDatePayload,
      buildNumberPayload,
      buildPayload,
    };

    return createPayloadByType(selectedVariable, payloadBuilders);
  };

  /**
   * Saves variables using API based on selectedVariable type
   * @author Abhishek Dhillon
   */
  const handleSaveVariables = async () => {
    if (!selectedVariable) {
      return;
    }
    try {
      // Create payload using separate function
      const payload = createVariablePayload();
      // Save using appropriate API based on workflow existence
      if (workflow?.Id) {
             await saveVariableForExistingWorkflow(payload);
      } else {
        await saveVariableForNewWorkflow(payload);
      }
      // Clear selected variable after successful save
      setSelectedVariable(null);
    } catch (error) {
      // Handle error appropriately
      console.error("Error saving variables:", error);
    }
  };

  const handlePreview = () => {
    // Future: Implement preview functionality
  };

  const handlePublish = () => {
    // Future: Implement publish functionality
  };

  /**
   * Builds table-specific properties
   * @author Abhishek Dhillon
   */
  const buildTableProperties = (isTable) => ({
    columns: isTable
      ? Array.from({ length: 4 }, (_, i) => ({
          id: i,
          field: `column${i + 1}`,
          title: `Column ${i + 1}`,
          type: "text",
        }))
      : [],
    columnCount: isTable ? 4 : 0,
  });

  /**
   * Builds workflow variable properties
   * @author Abhishek Dhillon
   */
  const buildWorkflowProperties = (workflowVariable, attributes) => ({
    workflowVariableId: workflowVariable ? workflowVariable?.id : null,
    isWorkflowVariable: workflowVariable ? true : false,
    ...(workflowVariable ? { ...attributes, options: attributes?.options } : {}),
  });

  /**
   * Builds base properties for any component
   * @author Abhishek Dhillon
   */
  const buildBaseProperties = ({ id, name, chipType }) => ({
    label: name,
    placeholder: id === "horizontal-line" ? "" : `Enter ${name.toLowerCase()}...`,
    ...(chipType && { chipType }),
    ...(["text-label", "advanced-text"].includes(id) && { value: "" }),
  });

  /**
   * Determines the element type based on component configuration
   * @param {Object} component - Component configuration object
   * @returns {string} The determined element type
   * @author Abhishek Dhillon
   */
  const determineElementType = (component) => {
    const { id, type, workflowVariable, variableId, variableType } = component;

    // Priority 1: Workflow variable type
    if (workflowVariable) {
      return type;
    }

    // Priority 2: User chip type resolution
    if (type === "user chip") {
      return variableId?.variableType || variableType;
    }

    // Priority 3: Default to component id
    return id;
  };

  /**
   * Builds user chip properties
   * @author Abhishek Dhillon
   */
  const buildUserChipProperties = (isUserChip, userChip) => {
    return {
      ...(isUserChip && {
        workflowVariableId: null,
        isWorkflowVariable: false,
        isUserVariable: true,
        userVariableId: userChip._id,
        fixedVariableId: userChip.fixedVariableId,
      }),
    };
  };

  const buildRowContainerProperties = (isRowContainer) => {
    return {
      ...(isRowContainer && {
        columnCount: 2,
        rowCount: 1,
        columnWidths: [50, 50],
        gap: 8,
        label: "Grid Layout",
        placeholder: "",
      }),
    };
  };

  const buildFormDimensionElementFromComponent = (component) => {
    const { workflowVariable, dimension } = component;
    const dimType = normalizeMultiDimensionType(dimension?.type);
    const chipName = workflowVariable?.name ?? "";
    const dimLabel = dimension?.label ?? "";
    const displayName = dimLabel ? `${workflowVariable.name} | ${dimLabel}` : chipName;
    return {
      id: generateId("element"),
      type: dimType,
      name: displayName,
      properties: {
        label: displayName,
        placeholder: "",
        isFormDimensionDisplay: true,
        parentWorkflowVariableId: workflowVariable.id,
        multiDimensionRowId: dimension.id,
        chipType: workflowVariable.chipType,
        isReadOnly: true,
        isWorkflowVariable: false,
      },
    };
  };

  /**
   * Builds form element from component configuration
   * @author Abhishek Dhillon
   */
  const buildElementFromComponent = (component) => {
    if (component.isWorkflowVariableDimension) {
      return buildFormDimensionElementFromComponent(component);
    }
    const { id, type, name, workflowVariable, attributes } = component;
    return {
      id: generateId("element"),
      type: determineElementType(component),
      name,
      properties: {
        ...buildBaseProperties(component),
        ...buildTableProperties(id === "table"),
        ...buildWorkflowProperties(workflowVariable, attributes),
        ...buildUserChipProperties(type === "user chip", component),
        ...buildRowContainerProperties(id === "row-container"),
      },
      ...(id === "row-container" && {
        gridElements: [
          Array(2).fill(null), // First row with 2 empty columns
        ],
      }),
    };
  };

  /**
   * Builds a new subsection object with a default text-label element.
   * @returns {{ id: string, title: string, elements: Array<Object> }} Subsection ready to append to form data
   * @author Abhishek Dhillon
   */
  const createNewSubsection = () => {
    const labelComponent = { id: FORM_ELEMENT_TYPES.TEXT_LABEL, name: "Text", icon: "T" };
    const defaultTextLabel = buildElementFromComponent(labelComponent);
    return {
      id: generateId("sub"),
      title: "",
      elements: [defaultTextLabel],
    };
  };

  /**
   * Appends a new subsection to the active host section and returns its id for expansion.
   * @returns {string} New subsection id
   * @author Abhishek Dhillon
   */
  const handleAddSubsection = () => {
    const { hostSection } = formTabs;
    const newSubsection = createNewSubsection();
    setFormData((prev) => ({ ...prev, [hostSection]: [...(prev?.[hostSection] || []), newSubsection] }));
    return newSubsection.id;
  };

  const handleDrop = (component, destination = { subsectionId: null }) => {
    const { hostSection } = formTabs;
    if (component?.isWorkflowVariableDimension) {
      const vid = component.workflowVariable?.id;
      const dimId = component.dimension?.id;
      if (!vid || !dimId) return;
      if (!collectUsedWorkflowVariableIds(formData, customWorkflowVariables).includes(vid)) return;
    }

    // Handle grid cell drops
    if (destination?.isRowDrop) {
      const newElement = buildElementFromComponent(component);
      setFormData((prev) => ({
        ...prev,
        [hostSection]: prev?.[hostSection]?.map((sub) => ({
          ...sub,
          elements: sub.elements.map((el) => {
            if (el.id === destination.rowElementId && el.type === "row-container") {
              // Add element to the specified grid cell [rowIndex][columnIndex]
              const updatedGridElements = [...(el.gridElements || [])];
              const { rowIndex, columnIndex } = destination;

              // Ensure the row exists
              if (updatedGridElements[rowIndex]) {
                updatedGridElements[rowIndex] = [...updatedGridElements[rowIndex]];
                updatedGridElements[rowIndex][columnIndex] = newElement;
              }

              return {
                ...el,
                gridElements: updatedGridElements,
              };
            }
            return el;
          }),
        })),
      }));
      return;
    }

    const newElement = buildElementFromComponent(component);
    // If dropped on a subsection, append there; else create a new subsection
    if (destination?.subsectionId) {
      setFormData((prev) => ({
        ...prev,
        [hostSection]: prev?.[hostSection]?.map((sub) =>
          sub.id === destination.subsectionId ? { ...sub, elements: [...sub.elements, newElement] } : sub
        ),
      }));
    } else {
      // Outside drop: create a new subsection. If dropdown, add a label first.
      let elementsForSubsection = [newElement];
      if ((newElement.properties.chipType === "smart chip" || newElement.type === "table") && !newElement.properties.isFormDimensionDisplay) {
        const labelComponent = { id: "text-label", name: "Text", icon: "T" };
        const labelElement = buildElementFromComponent(labelComponent);
        elementsForSubsection = [labelElement, newElement];
      }
      const newSubsection = {
        id: generateId("sub"),
        title: "",
        elements: elementsForSubsection,
      };
      setFormData((prev) => ({
        ...prev,
        [hostSection]: [...(prev?.[hostSection] || []), newSubsection],
      }));

      // Return the new subsection ID so it can be expanded
      return newSubsection.id;
    }
  };

  const handleLabelChange = (elementId, value) => {
    const { hostSection } = formTabs;
    setFormData((prev) => {
      const newFormData = {
        ...prev,
        [hostSection]: prev?.[hostSection]?.map((sub) => ({
          ...sub,
          elements: sub.elements.map((el) => {
            if (el.id === elementId && (el.type === "text-label" || el.type === "advanced-text")) {
              const updatedElement = { ...el, properties: { ...el.properties, value } };
              // Update selectedElement if it's the one being modified
              if (selectedElement && selectedElement.id === elementId) {
                setSelectedElement(updatedElement);
              }
              return updatedElement;
            }
            return el;
          }),
        })),
      };
      return newFormData;
    });
  };

  const handleFieldPropertiesChange = (elementId, partialProps) => {
    const { hostSection } = formTabs;
    // Handle special case: grid cell deletion
    if (elementId === "GRID_CELL_DELETE") {
      const { elementId: targetElementId, rowIndex, columnIndex } = partialProps;
      setFormData((prev) => ({
        ...prev,
        [hostSection]: prev?.[hostSection]?.map((sub) => ({
          ...sub,
          elements: sub.elements.map((el) => {
            if (el.type === "row-container" && el.gridElements) {
              // Check if this grid contains the element to delete
              const hasElement = el.gridElements.some((row) => row.some((cell) => cell && cell.id === targetElementId));

              if (hasElement) {
                const updatedGridElements = [...el.gridElements];
                if (updatedGridElements[rowIndex]) {
                  updatedGridElements[rowIndex] = [...updatedGridElements[rowIndex]];
                  updatedGridElements[rowIndex][columnIndex] = null;
                }
                return { ...el, gridElements: updatedGridElements };
              }
            }
            return el;
          }),
        })),
      }));
      return;
    }

    setFormData((prev) => {
      // For smart chip linking, we need the original table element BEFORE updating
      let originalTableElement = null;
      if (partialProps.columns && elementId) {
        // Find the original table element before any updates (including in row containers)
        prev?.[hostSection]?.forEach((sub) => {
          sub.elements.forEach((el) => {
            if (el.id === elementId) {
              originalTableElement = el;
            } else if (el.type === "row-container" && el.gridElements) {
              // Search within grid container elements
              el.gridElements.forEach((row) => {
                row.forEach((cell) => {
                  if (cell && cell.id === elementId) {
                    originalTableElement = cell;
                  }
                });
              });
            }
          });
        });
      }

      const newFormData = {
        ...prev,
        [hostSection]: prev?.[hostSection]?.map((sub) => ({
          ...sub,
          elements: sub.elements.map((el) => {
            // Handle row container property updates FIRST (before regular elements)
            if (el.id === elementId && el.type === "row-container") {
              const updatedElement = { ...el };
              if (partialProps?.type === "showIf") {
                if (partialProps?.showIf !== undefined) {
                  updatedElement.showIf = partialProps?.showIf;
                }
                if (partialProps?.showIfExpression !== undefined) {
                  updatedElement.showIfExpression = partialProps?.showIfExpression;
                }
              } else {
                updatedElement.properties = { ...el.properties, ...partialProps };
              }
              // If column count changed, adjust all rows to fit new count
              if (partialProps.columnCount !== undefined && typeof partialProps.columnCount === "number") {
                const newColumnCount = partialProps.columnCount;
                const updatedGridElements = (el.gridElements || []).map((row) => {
                  if (row.length > newColumnCount) {
                    // Trim excess columns
                    return row.slice(0, newColumnCount);
                  } else if (row.length < newColumnCount) {
                    // Add empty columns
                    return [...row, ...Array(newColumnCount - row.length).fill(null)];
                  }
                  return row;
                });
                updatedElement.gridElements = updatedGridElements;
              }

              // If row count changed, adjust the grid
              if (partialProps.rowCount !== undefined && typeof partialProps.rowCount === "number") {
                const newRowCount = partialProps.rowCount;
                const currentGridElements = el.gridElements || [];
                const columnCount = el.properties?.columnCount || 2;

                if (currentGridElements.length > newRowCount) {
                  // Remove excess rows
                  updatedElement.gridElements = currentGridElements.slice(0, newRowCount);
                } else if (currentGridElements.length < newRowCount) {
                  // Add new empty rows
                  const newGridElements = [...currentGridElements];
                  for (let i = currentGridElements.length; i < newRowCount; i++) {
                    newGridElements.push(Array(columnCount).fill(null));
                  }
                  updatedElement.gridElements = newGridElements;
                }
              }

              // Update selectedElement if it's the one being modified
              if (selectedElement && selectedElement.id === elementId) {
                setSelectedElement(updatedElement);
              }
              return updatedElement;
            }

            // Handle regular elements (non-grid containers)
            if (el.id === elementId) {
              let updatedElement = { ...el };
              if (partialProps?.type === "showIf") {
                if (partialProps?.showIf !== undefined) {
                  updatedElement.showIf = partialProps?.showIf;
                }
                if (partialProps?.showIfExpression !== undefined) {
                  updatedElement.showIfExpression = partialProps?.showIfExpression;
                }
              } else {
                updatedElement.properties = { ...el.properties, ...partialProps };
              }
              // Update selectedElement if it's the one being modified
              if (selectedElement && selectedElement.id === elementId) {
                setSelectedElement(updatedElement);
              }
              return updatedElement;
            }

            // Handle elements within grid containers (grid cells)
            if (el.type === "row-container" && el.gridElements) {
              const updatedGridElements = el.gridElements.map((row) =>
                row.map((cell) => {
                  if (cell && cell.id === elementId) {
                    let updatedElement = { ...cell };
                    if (partialProps?.type === "showIf") {
                      if (partialProps?.showIf !== undefined) {
                        updatedElement.showIf = partialProps?.showIf;
                      }
                      if (partialProps?.showIfExpression !== undefined) {
                        updatedElement.showIfExpression = partialProps?.showIfExpression;
                      }
                    } else {
                      updatedElement.properties = { ...updatedElement.properties, ...partialProps };
                    }
                    if (selectedElement && selectedElement.id === elementId) {
                      setSelectedElement(updatedElement);
                    }
                    return updatedElement;
                  }
                  return cell;
                })
              );
              // Check if any updates were made
              const hasChanges = updatedGridElements.some((row, rowIndex) =>
                row.some((cell, colIndex) => cell !== el.gridElements[rowIndex][colIndex])
              );
              if (hasChanges) {
                return { ...el, gridElements: updatedGridElements };
              }
            }
            return el;
          }),
        })),
      };

      // Handle smart chip linking for table columns
      if (partialProps.columns && elementId && originalTableElement) {
        return handleSmartChipLinking(newFormData, elementId, partialProps.columns, originalTableElement);
      }

      return newFormData;
    });
  };

  // Create or remove hidden workflow variable elements when smart chips are linked/unlinked
  const handleSmartChipLinking = (formData, tableElementId, updatedColumns, originalTableElement) => {
    const { hostSection } = formTabs;
    const result = { ...formData };

    // Find the table element and its subsection
    let tableSubsectionIndex = -1;
    let tableElement = null;

    result?.[hostSection]?.forEach((sub, subIndex) => {
      const foundElement = sub.elements.find((el) => el.id === tableElementId);
      if (foundElement) {
        tableElement = foundElement;
        tableSubsectionIndex = subIndex;
      }
    });

    if (!tableElement || tableSubsectionIndex === -1) return result;

    const subsection = result?.[hostSection]?.[tableSubsectionIndex];

    // Process each column for smart chip linking
    updatedColumns.forEach((column, columnIndex) => {
      const originalColumn = originalTableElement.properties.columns?.[columnIndex];
      const linkedVariableId = column.linkedSmartChipId;
      const hadLink = originalColumn?.linkedSmartChipId;

      // If column type changed from number to non-number, automatically clear smart chip link
      const wasNumber = originalColumn?.type === "number";
      const isNumber = column.type === "number";
      const typeChangedFromNumber = wasNumber && !isNumber;

      // Clear linkedSmartChipId if type changed from number to non-number
      let effectiveLinkedVariableId = linkedVariableId;
      if (typeChangedFromNumber && linkedVariableId) {
        effectiveLinkedVariableId = null;
      }

      // If column now has a linked smart chip (and wasn't linked before)
      if (effectiveLinkedVariableId && effectiveLinkedVariableId !== hadLink) {
        // Remove any existing hidden element for this variable
        subsection.elements = subsection.elements.filter(
          (el) => !(el.properties?.isHidden && el.properties?.workflowVariableId === effectiveLinkedVariableId)
        );

        // Find the workflow variable
        const workflowVariable = customWorkflowVariables?.find((v) => v.id === effectiveLinkedVariableId);
        if (workflowVariable) {
          // Create hidden workflow variable element
          const hiddenElement = {
            id: generateId("element"),
            type: workflowVariable.type,
            name: workflowVariable.name,

            properties: {
              workflowVariableId: effectiveLinkedVariableId,
              isWorkflowVariable: true,
              isHidden: true, // Mark as hidden
              sourceTable: tableElementId,
              sourceColumn: columnIndex,
              operation: column.operation || "addition",
              label: `${workflowVariable.name} (Linked to Table)`,
              ...(workflowVariable.attributes || {}),
            },
            value: 0, // Initial value
          };

          // Add hidden element to the same subsection as the table
          subsection.elements.push(hiddenElement);
        }
      }

      // If column no longer has a linked smart chip (was removed or type changed)
      else if (!effectiveLinkedVariableId && hadLink) {
        // Remove the hidden element
        subsection.elements = subsection.elements.filter((el) => !(el.properties?.isHidden && el.properties?.workflowVariableId === hadLink));
      }
    });

    // Update the column data to clear linkedSmartChipId for non-number columns
    const finalResult = { ...result };
    finalResult.subsections[tableSubsectionIndex].elements = finalResult.subsections[tableSubsectionIndex].elements.map((el) => {
      if (el.id === tableElementId) {
        const updatedColumns = el.properties.columns.map((col, colIndex) => {
          // Clear linkedSmartChipId if column is not a number type
          if (col.type !== "number" && col.linkedSmartChipId) {
            return {
              ...col,
              linkedSmartChipId: null,
              showLinkDropdown: false,
            };
          }
          return col;
        });

        return {
          ...el,
          properties: {
            ...el.properties,
            columns: updatedColumns,
          },
        };
      }
      return el;
    });

    return finalResult;
  };

  // Handle subsection properties change (name, color)
  const handleSubsectionPropertiesChange = (subsectionId, partialProps) => {
    setFormData((prev) => ({
      ...prev,
      [formTabs?.hostSection]: prev[formTabs?.hostSection].map((sub) => {
        if (sub.id === subsectionId) {
          let updatedSubsection = { ...sub, ...partialProps };
          setSelectedSubsection(updatedSubsection);
          return updatedSubsection;
        }
        return sub;
      }),
    }));
  };

  // Handle form background color change
  const handleFormBgColorChange = (key, bgColor) => {
    setFormData((prev) => ({
      ...prev,
      [key]: bgColor,
    }));
  };

  const handleElementSelect = (element) => {
    setSelectedElement(element);
    if (element?.properties?.isFormDimensionDisplay) {
      setSelectedVariable(null);
      return;
    }
    const chipLinkId = element?.smartChipId ?? element?.properties?.workflowVariableId;
    const wfVariable =
      chipLinkId != null ? customWorkflowVariables?.find((v) => String(v.id) === String(chipLinkId) || String(v._id) === String(chipLinkId)) : null;
    if (element?.properties?.chipType !== "smart chip") setSelectedVariable(null);
    else {
      setIsNewWorkflowVariable(false);
      setSelectedVariable(wfVariable ?? null);
      if (wfVariable) {
        setSelectedVariableValues(wfVariable, wfVariable?.name);
      }
    }
  };

  const handleElementDelete = (elementId) => {
    setFormData((prev) => ({
      ...prev,
      [formTabs?.hostSection]: prev[formTabs?.hostSection]
        .map((sub) => ({ ...sub, elements: sub.elements.filter((el) => el?.id !== elementId) }))
        .filter((sub) => sub.elements.length > 0), // remove empty subsections
    }));

    // // Clear selection if deleted element was selected
    // if (selectedElement.id === elementId) {
    //   setSelectedElement(null);
    // }
  };

  /**
   * Removes an element from the form by its workflowVariableId
   * Used by "Click to remove from form" button in configuration panels
   * @param {number|string} workflowVariableId - The workflow variable ID to remove
   */
  const handleRemoveElementByVariableId = (workflowVariableId) => {
    // Find the element with matching workflowVariableId
    let elementIdToDelete = null;
    formData?.[formTabs?.hostSection]?.forEach((sub) => {
      sub.elements.forEach((el) => {
        if (el.properties?.workflowVariableId === workflowVariableId) {
          elementIdToDelete = el.id;
        }
      });
    });

    if (elementIdToDelete) {
      handleElementDelete(elementIdToDelete);
    }
  };

  const handleSubsectionDelete = (subsectionId) => {
    setFormData((prev) => ({
      ...prev,
      [formTabs?.hostSection]: prev[formTabs?.hostSection].filter((sub) => sub.id !== subsectionId),
    }));
    // Clear selection if any element from deleted subsection was selected
    if (selectedElement) {
      const deletedSubsection = formData?.[formTabs?.hostSection]?.find((sub) => sub.id === subsectionId);
      const elementInDeletedSubsection = deletedSubsection?.elements.find((el) => el.id === selectedElement.id);
      if (elementInDeletedSubsection) {
        setSelectedElement(null);
      }
    }
  };

  const formCanvasElement = (
    <FormCanvas
      formData={formData}
      setFormData={setFormData}
      onDrop={handleDrop}
      onElementSelect={handleElementSelect}
      onElementDelete={handleElementDelete}
      onSubsectionDelete={handleSubsectionDelete}
      onAddSubsection={handleAddSubsection}
      onSave={handleSave}
      onCancel={handleCloseModal}
      onLabelChange={handleLabelChange}
      saveButtonText={isNewForm ? "Save Form" : "Update Form"}
      onFieldPropertiesChange={handleFieldPropertiesChange}
      isEditable={isEditable}
      onSaveVariables={handleSaveVariables}
      formTabs={formTabs}
    />
  );

  const onTabSelect = (e) => {
    setFormTabs((prev) => ({
      ...prev,
      selected: e.selected,
      ...FORM_TABS[FORM_TABS_KEYS[e?.selected]],
    }));
  };

  const renderFormCanvasArea = () => {
    if (formTabs?.isHiddenSection) {
      return (
        <CommonTabStrip
          tabs={[
            { title: FORM_TABS.SUBSECTIONS_OBJ.title, content: formCanvasElement },
            { title: FORM_TABS.HIDDEN_SUBSECTIONS_OBJ.title, content: formCanvasElement },
          ]}
          selectedTab={formTabs?.selected}
          onTabSelect={onTabSelect}
          contentClassName='form-builder-layout'
          animation={false}
          tabStripProps={{ keepTabsMounted: true, renderAllContent: true }}
        />
      );
    }
    return formCanvasElement;
  };

  return (
    <div className='form-builder d-flex flex-column'>
      {/* Form Builder Header */}
      <FormBuilderHeader
        handleCloseModal={handleCloseModal}
        formId={formId}
        isNewForm={isNewForm}
        formData={formData}
        currentFormData={currentFormData}
        isEditable={isEditable}
        setIsEditable={setIsEditable}
        projectId={projectIdParam || projectId}
        onFormBgColorChange={handleFormBgColorChange}
        onSave={handleSave}
        onSaveVariables={handleSaveVariables}
        formTabs={formTabs}
        setFormTabs={setFormTabs}
        workflowId={workflowId}
        onNavigateToWorkflowEditor={onNavigateToWorkflowEditor}
        openFormSettingsOnRestore={openFormSettingsOnRestore}
        onConsumeOpenFormSettingsRestore={onConsumeOpenFormSettingsRestore}
      />

      {/* Main Layout */}
      <div className='form-builder-layout with-footer'>
        <FormBuilderSidebar
          usedWorkflowVariableIds={collectUsedWorkflowVariableIds(formData, customWorkflowVariables)}
          workflowId={workflowId}
          projectId={projectIdParam || projectId}
          formId={formId}
          formData={formData}
        />
        {renderFormCanvasArea()}
        <FormPropertiesWrapper
          onFieldPropertiesChange={handleFieldPropertiesChange}
          onSubsectionPropertiesChange={handleSubsectionPropertiesChange}
          usedWorkflowVariableIds={collectUsedWorkflowVariableIds(formData, customWorkflowVariables)}
          onRemoveElementByVariableId={handleRemoveElementByVariableId}
          formData={formData}
          formId={formId}
        />
      </div>
    </div>
  );
};

/**
 * Main Form Builder Component with Context Providers
 * Orchestrates all the different sections and manages state with split contexts
 */
const FormBuilder = ({
  initialTitle,
  initialDescription,
  isNewForm,
  projectIdParam,
  onNavigateToWorkflowEditor,
  openFormSettingsOnRestore,
  onConsumeOpenFormSettingsRestore,
  ...props
}) => {
  const { projectId } = useParams();
  const { myProjectId } = useSelector((state) => state.auth.user);
  const resolvedProjectId = projectIdParam ?? projectId;
  const isMyBoardNewForm = isNewForm && resolvedProjectId != null && myProjectId != null && Number(resolvedProjectId) === Number(myProjectId);
  const myBoardInitialPrivacy = { value: number.THREE, label: "Team", Icon: "TASK_LOCK" };

  return (
    <FormMetadataProvider
      initialTitle={initialTitle}
      initialDescription={initialDescription}
      initialPrivacy={isMyBoardNewForm ? myBoardInitialPrivacy : undefined}>
      <FormChangeProvider>
        <FormCalculationProvider>
          <FormProvider>
            <VariableContextProvider>
              <FormBuilderContent
                initialTitle={initialTitle}
                initialDescription={initialDescription}
                isNewForm={isNewForm}
                projectIdParam={projectIdParam}
                onNavigateToWorkflowEditor={onNavigateToWorkflowEditor}
                openFormSettingsOnRestore={openFormSettingsOnRestore}
                onConsumeOpenFormSettingsRestore={onConsumeOpenFormSettingsRestore}
                {...props}
              />
            </VariableContextProvider>
          </FormProvider>
        </FormCalculationProvider>
      </FormChangeProvider>
    </FormMetadataProvider>
  );
};

export default FormBuilder;
